import { HttpClient, HttpErrorResponse } from '@angular/common/http'
import { Observable, catchError, map, of } from 'rxjs'
import { Injectable, inject } from '@angular/core'

import { RiskCapture, RiskCaptureHistory, RiskScorecardView, SaveRiskCapture } from '@data/interfaces/risk-scorecard.interface'
import { SnapshotContext, SnapshotResponse } from '@data/interfaces/verification-snapshot.interface'
import { environment } from '@env/environment'

const CONNECTION_ERROR = 'Hubo un error de conexión'

/**
 * Calculadora de riesgo (plan v4.2 §6.3).
 *
 *   GET /{clients|leases}/:id/risk-scorecard       · resultado de la calculadora, con estados financieros, ratios y flujos
 *   GET /leases/:id/risk-scorecard/capture         · captura vigente de C5 y Colateral, con las anteriores
 *   PUT /leases/:id/risk-scorecard/capture         · capturar (motivo obligatorio; 409 `LEASE_FROZEN` si está congelado)
 *
 * Como `ApiVerificationSnapshotService`: ningún método lanza; el error viaja en el sobre con `status` y `code`, que es
 * lo que la pestaña usa para distinguir «sin permiso» de «falló la red».
 */
@Injectable({ providedIn: 'root' })
export class ApiRiskScorecardService {
  private readonly http = inject(HttpClient)

  get(ctx: SnapshotContext): Observable<SnapshotResponse<RiskScorecardView>> {
    return this.wrap(this.http.get(this.url(ctx)), 'No pudimos cargar la calculadora.')
  }

  getCapture(leaseId: string): Observable<SnapshotResponse<RiskCaptureHistory>> {
    return this.wrap(this.http.get(`${environment.apiUrl}leases/${leaseId}/risk-scorecard/capture`), 'No pudimos cargar la captura.')
  }

  saveCapture(leaseId: string, body: SaveRiskCapture): Observable<SnapshotResponse<RiskCapture>> {
    return this.wrap(this.http.put(`${environment.apiUrl}leases/${leaseId}/risk-scorecard/capture`, body), 'No se pudo guardar la captura.')
  }

  private url(ctx: SnapshotContext): string {
    return `${environment.apiUrl}${ctx.scope === 'lease' ? 'leases' : 'clients'}/${ctx.id}/risk-scorecard`
  }

  private wrap<T>(source: Observable<unknown>, fallback: string): Observable<SnapshotResponse<T>> {
    return source.pipe(
      map((r: any) => ({ error: !!r?.error, msg: r?.msg ?? '', data: (r?.data ?? null) as T | null, status: 200 })),
      catchError((e: HttpErrorResponse) => {
        const status = e?.status ?? 0
        if (status === 0) return of({ error: true, msg: CONNECTION_ERROR, data: null, status, code: 'NETWORK' })
        //* El filtro de la API manda el motivo de negocio en `msg` (el 409 `LEASE_FROZEN`, el 400 de motivo vacío); el toast lo muestra tal cual.
        const body = e?.error
        return of({ error: true, msg: body?.msg ?? fallback, data: null, status, code: body?.code ?? (status === 403 ? 'FORBIDDEN' : undefined) })
      }),
    )
  }
}
